/**
 * "Getting to the airport" (register P5). The owner's home, how they usually get to
 * the airport, and what that costs per airport, with parking read from each
 * airport's own page. Unknown stays unknown and says so. Only the owner sees this.
 */
import { useEffect, useState } from "react";
import { Car, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { homeApi } from "@/lib/api";
import type { AirportParking, HomeSettings, HomeAirportRow, UsualMode } from "@/lib/types";

const MODES: { value: UsualMode; label: string }[] = [
  { value: "drive", label: "Drive and park" },
  { value: "ride", label: "Take Uber or Lyft" },
  { value: "cheaper", label: "Whichever costs less" },
];

const money = (n: number) => "$" + (Number.isInteger(n) ? String(n) : n.toFixed(2));

export const PRIVACY_LINE = "We send your address to the US Census geocoder once, to find it on a map. Only you can see it.";

export const RATES_LINE = (a: HomeSettings["assumptions"]): string =>
  `Miles are about: a straight line times ${a.roadFactor}, not a route. Driving uses the IRS standard mileage rate, ${Math.round(a.irsDollarsPerMile * 100)} cents a mile for July to December 2026. ` +
  `Rides are estimates, not Uber or Lyft quotes. We use ${money(a.rideBase)} to start, ${money(a.ridePerMile)} a mile and ${money(a.ridePerMinute)} a minute at ${a.assumedMph} mph.`;

const readDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
const apiError = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** One airport's parking, in plain words. Exported for tests. */
export function ParkingBlock({ code, parking }: { code: string; parking: AirportParking }) {
  const source = parking.sourceUrl && (
    <a href={parking.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline">
      the airport's page
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
  let body;
  if (parking.state === "researching") {
    body = <p className="text-xs text-muted-foreground">Still researching. We read one airport at a time, so this can take a while.</p>;
  } else if (parking.state === "no_page") {
    body = <p className="text-xs text-muted-foreground">We have no parking page on file for this airport yet. We do not guess.</p>;
  } else if (parking.state === "refused") {
    body = <p className="text-xs text-muted-foreground">The airport's site refused our reader on {readDate(parking.lastTriedAt)}. We do not guess. You can check {source} yourself.</p>;
  } else if (parking.state === "failed") {
    body = <p className="text-xs text-muted-foreground">We could not read the airport's site on {readDate(parking.lastTriedAt)}. We will try again. We do not guess.</p>;
  } else if (parking.state === "no_rate" && parking.options.length === 0) {
    body = <p className="text-xs text-muted-foreground">No published rate found. We do not guess. Read {readDate(parking.readAt)}.</p>;
  } else {
    body = (
      <>
        <ul className="divide-y divide-border text-sm">
          {parking.options.map((o) => (
            <li key={o.name} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="min-w-0">
                <a href={o.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">{o.name}</a>
                {o.sourceKind === "third_party" && (
                  <span className="block text-xs text-muted-foreground">third-party, confirm on the airport's page</span>
                )}
              </span>
              <span className="shrink-0 tabular-nums">{o.pricePerDay != null ? money(o.pricePerDay) + " a day" : "No published rate"}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">Read from {source} on {readDate(parking.readAt)}.</p>
      </>
    );
  }
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-foreground">Parking at {code}, per day</p>
      {body}
    </div>
  );
}

export function AirportTable({ airports }: { airports: HomeAirportRow[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/40 text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="px-2 py-2 font-medium">Airport</th>
            <th scope="col" className="px-1.5 py-2 text-right font-medium">Miles, about</th>
            <th scope="col" className="px-1.5 py-2 text-right font-medium">Drive, both ways</th>
            <th scope="col" className="px-1.5 py-2 text-right font-medium">Rides, both ways</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {airports.map((a) => (
            <tr key={a.code}>
              <td className="px-2 py-2 align-top">
                <span className="font-medium text-foreground">{a.code}</span>
                <span className="block text-xs text-muted-foreground">{a.name}</span>
              </td>
              <td className="px-1.5 py-2 text-right align-top tabular-nums">{a.miles}</td>
              <td className="px-1.5 py-2 text-right align-top tabular-nums">{money(a.driveBothWays)}</td>
              <td className="px-1.5 py-2 text-right align-top tabular-nums">{money(a.ridesBothWays)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GettingToAirportCard() {
  const [data, setData] = useState<HomeSettings | null>(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const apply = (d: HomeSettings) => {
    setData(d);
    setAddress(d.home?.address ?? "");
  };

  useEffect(() => {
    homeApi
      .get()
      .then(apply)
      .catch(() => setMessage("We could not load this. Try again."))
      .finally(() => setLoading(false));
  }, []);

  const run = async (work: () => Promise<HomeSettings>, done: string, fallback: string) => {
    setBusy(true);
    setMessage(null);
    try {
      apply(await work());
      setMessage(done);
    } catch (e) {
      setMessage(apiError(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const home = data?.home ?? null;
  const a = data?.assumptions;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" aria-hidden="true" />
          Getting to the airport
        </CardTitle>
        <CardDescription>
          Getting there counts in your best fare, but not in your trip's headline price. A cost you type in How I fly still wins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading" />
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="home-address">Home address</Label>
              <Input
                id="home-address"
                autoComplete="street-address"
                maxLength={200}
                className="h-11"
                placeholder="Street, city, state"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{PRIVACY_LINE}</p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  className="min-h-11"
                  size="sm"
                  disabled={busy || !address.trim() || address.trim() === home?.address}
                  onClick={() => run(() => homeApi.save(address.trim()), "Saved. Your trips use this now.", "That did not save. Try again.")}
                >
                  {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Save address
                </Button>
                {home && (
                  <Button
                    className="min-h-11"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => run(() => homeApi.remove(), "Deleted. We no longer have your address.", "That did not delete. Try again.")}
                  >
                    <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                    Delete address
                  </Button>
                )}
              </div>
              <p role="status" className="text-xs text-muted-foreground">{message}</p>
            </div>

            {!home ? (
              <p className="text-sm text-muted-foreground">
                Add your address to see the miles, drive cost, ride cost and parking at airports near you.
              </p>
            ) : (
              <>
                <fieldset className="space-y-1.5">
                  <legend className="text-sm font-medium text-foreground">I usually</legend>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {MODES.map((m) => (
                      <Button
                        key={m.value}
                        type="button"
                        className="min-h-11"
                        size="sm"
                        variant={home.usualMode === m.value ? "default" : "outline"}
                        aria-pressed={home.usualMode === m.value}
                        disabled={busy}
                        onClick={() => run(() => homeApi.setMode(m.value), "Saved. Your trips use this now.", "That did not save. Try again.")}
                      >
                        {m.label}
                      </Button>
                    ))}
                  </div>
                </fieldset>

                {data && data.airports.length > 0 ? (
                  <AirportTable airports={data.airports} />
                ) : (
                  <p className="text-sm text-muted-foreground">We do not have an airport on our map within 100 miles of your home yet.</p>
                )}

                {a && (
                  <p className="text-xs text-muted-foreground">
                    {RATES_LINE(a)}{" "}
                    <a href="https://www.irs.gov/tax-professionals/standard-mileage-rates" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                      See the IRS rate
                    </a>
                    .
                  </p>
                )}

                {data?.airports.map((row) => (
                  <div key={row.code} className="space-y-2 border-t border-border pt-3">
                    <ParkingBlock code={row.code} parking={row.parking} />
                    <p className="text-xs text-muted-foreground">
                      {row.airlinesSeen.length > 0
                        ? `Airlines we have seen here: ${row.airlinesSeen.join(", ")}. This is from your trips, not a schedule.`
                        : "Airlines we have seen here: none on your trips yet."}
                    </p>
                  </div>
                ))}

                {data && data.notCounted.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Also on your trips, too far from home or not on our map, so not counted: {data.notCounted.join(", ")}.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
